import {
  Trash,
  XSquareFill,
  ArrowCounterclockwise,
} from 'react-bootstrap-icons';
import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Button, Form } from 'react-bootstrap';
import { useDirtyFormFieldClass } from '@/common/contexts/DirtyFormFieldClassContext.tsx';

interface FileUploadFieldProps {
  name: string;
  accept?: string;
  onChange?: (file: File | null) => void;
  existingFile?: string | null;
  onDeleteExisting?: () => void;
  onRestoreExisting?: () => void;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  name,
  accept,
  onChange,
  existingFile,
  onDeleteExisting,
  onRestoreExisting,
}) => {
  const props = { name, accept, onChange };

  const {
    control,
    setValue,
    resetField,
    formState: { errors },
  } = useFormContext();

  const getDirtyClass = useDirtyFormFieldClass();

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [hasFileInput, setHasFileInput] = React.useState(false);
  const [hasDeletedExistingFile, setHasDeletedExistingFile] =
    React.useState(false);

  function handleFileInputChange(
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (file: File | null) => void
  ) {
    const file = e.target.files?.[0] ?? null;
    setHasFileInput(!!file);
    onChange(file);
    if (props.onChange) {
      props.onChange(file);
    }
  }

  function handleClearUploadInput(onChange: (file: File | null) => void) {
    if (inputRef.current) inputRef.current.value = '';
    setHasFileInput(false);
    resetField(name, { defaultValue: null });
    handleUndoDeleteExistingFile();
    onChange(null);
    if (props.onChange) {
      props.onChange(null);
    }
  }

  function handleDeleteExistingFile() {
    resetField(name, { defaultValue: '__cleared__' });
    setValue(name, null, { shouldDirty: true });

    setHasDeletedExistingFile(true);
    if (onDeleteExisting) onDeleteExisting();
  }

  function handleUndoDeleteExistingFile() {
    if (!hasDeletedExistingFile) {
      return;
    }

    setHasFileInput(false);
    resetField(name, { defaultValue: null });

    setHasDeletedExistingFile(false);
    if (onRestoreExisting) onRestoreExisting();
  }

  function renderExistingFileControls() {
    if (!existingFile || hasFileInput) {
      return null;
    }

    if (hasDeletedExistingFile) {
      return (
        <Button
          variant="link"
          size="sm"
          className="me-4 p-0 border-0 d-flex align-items-center"
          onClick={handleUndoDeleteExistingFile}
          aria-label="Undo delete"
          title="Undo delete"
        >
          <ArrowCounterclockwise size={25} />
        </Button>
      );
    } else {
      return (
        <Button
          variant="link"
          size="sm"
          className="me-4 p-0 border-0 text-danger d-flex align-items-center"
          onClick={handleDeleteExistingFile}
          aria-label="Delete file"
          title="Delete file"
        >
          <Trash size={20} />
        </Button>
      );
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className="d-flex flex-column gap-1">
          <div className="d-flex align-items-center gap-2">
            {renderExistingFileControls()}
            <Form.Control
              ref={inputRef}
              type="file"
              className={`form-control ${getDirtyClass(name)}`}
              isInvalid={!!errors[name]}
              accept={accept}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFileInputChange(e, field.onChange)
              }
            />
            {hasFileInput && (
              <Button
                variant="link"
                size="sm"
                className="p-0 border-0 d-flex align-items-center"
                onClick={() => handleClearUploadInput(field.onChange)}
                aria-label="Clear file"
                title="Clear file"
              >
                <XSquareFill size={20} />
              </Button>
            )}
          </div>
          {errors[name]?.message && (
            <Form.Control.Feedback type="invalid" className="d-block">
              {errors[name]?.message?.toString()}
            </Form.Control.Feedback>
          )}
        </div>
      )}
    />
  );
};

export default FileUploadField;
